import { useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/shared/context/ToastContext';

export const useAdminNotifications = () => {
    const { addToast } = useToast();

    // Refs to track initial load for each listener
    const isFirstUsers = useRef(true);
    const isFirstTransactions = useRef(true);
    const isFirstKYC = useRef(true);
    const isFirstSupport = useRef(true);

    // 1. User Approvals (New Signups)
    useEffect(() => {
        const q = query(
            collection(db, 'users'),
            where('approved', '==', false)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (isFirstUsers.current) {
                isFirstUsers.current = false;
                return;
            }

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    addToast('info', `새로운 회원 가입 요청: ${data.name || data.email}`, 5000);
                }
            });
        }, (error) => console.error("User notification error:", error));

        return () => unsubscribe();
    }, [addToast]);

    // 2. Transaction Requests (Deposit/Withdrawal)
    useEffect(() => {
        const q = query(
            collection(db, 'transactionRequests'),
            where('status', '==', 'pending')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (isFirstTransactions.current) {
                isFirstTransactions.current = false;
                return;
            }

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const type = data.type === 'deposit' ? '입금' : '출금';
                    const amount = data.amount?.toLocaleString() || '0';
                    const currency = data.currency || '';
                    addToast('warning', `새로운 ${type} 신청: ${data.userName} (${amount} ${currency})`, 5000);
                }
            });
        }, (error) => console.error("Transaction notification error:", error));

        return () => unsubscribe();
    }, [addToast]);

    // 3. KYC Applications
    useEffect(() => {
        const q = query(
            collection(db, 'applications'),
            where('status', '==', 'PENDING')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (isFirstKYC.current) {
                isFirstKYC.current = false;
                return;
            }

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const typeMap: Record<string, string> = {
                        'PHONE': '휴대폰',
                        'ACCOUNT': '계좌',
                        'ID_CARD': '신분증',
                        'SELFIE': '셀카',
                        'CREDIT_CARD': '신용카드',
                        'ADDRESS': '거주지',
                        'BUSINESS': '사업자'
                    };
                    const type = typeMap[data.type] || data.type;
                    addToast('info', `새로운 KYC 인증 신청: ${type}`, 5000);
                }
            });
        }, (error) => console.error("KYC notification error:", error));

        return () => unsubscribe();
    }, [addToast]);

    // 4. Support Tickets
    useEffect(() => {
        const q = query(
            collection(db, 'support_tickets'),
            where('status', '==', 'pending')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (isFirstSupport.current) {
                isFirstSupport.current = false;
                return;
            }

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    addToast('info', `새로운 1:1 문의: ${data.subject}`, 5000);
                }
            });
        }, (error) => console.error("Support notification error:", error));

        return () => unsubscribe();
    }, [addToast]);
};
